import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState } from "recoil";
import CancelButton from "../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import { toast } from "../../../../components/toastNotification";
import {
  addShowState,
  editState,
  jobSaving,
  rowForEdit,
  scriptChange,
} from "../atoms/atoms";
import useAddJobs from "../hooks/useAddJobs";
import useEditJobs from "../hooks/useEditJobs";
import { IJobsDetails, IReportsDetails } from "../types";
import JobFormDetails from "./JobFormDetails";

interface JobFormProps {
  scheduleDaysOpts: [];
  monthDaysOpts: any[];
  refetchJobs;
}

export const JobForm: React.FC<JobFormProps> = ({
  scheduleDaysOpts,
  monthDaysOpts,
  refetchJobs,
}) => {
  const [editForRow, setRowToEdit] = useRecoilState(rowForEdit);
  const useFormMethods = useForm<IJobsDetails>({
    defaultValues: { ...editForRow },
  });
  const [actionAddState, setActionAddState] = useRecoilState(addShowState);
  const [, setActionEditState] = useRecoilState(editState);
  const [isSaving, setIsSaving] = useRecoilState(jobSaving);
  const [saveModal, setSaveModal] = useState(false);
  const [IsScriptChange, setIsScriptChange] = useRecoilState(scriptChange);
  const [newData, setNewData] = useState<IJobsDetails>();
  const queryClient = useQueryClient();
  const {
    formState: { isDirty },
    handleSubmit,
    setValue,
  } = useFormMethods;
  const {
    mutate: addJobs,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddJobs();

  const {
    mutate: updateJobs,
    isSuccess: editSuccess,
    isError: editError,
    data: editResult,
    reset: editResetData,
  } = useEditJobs();

  const onSubmit = (data: IJobsDetails) => {
    if (isDirty === true || IsScriptChange) {
      duplicateChechker(data);
    } else {
      toast.fire("Saving Failed", "No Changes have been made", "warning");
    }
  };
  const duplicateChechker = (data: IJobsDetails) => {
    let hasDuplicates = false;
    const reportsdetailsRow = [] as Array<IReportsDetails>;
    if (data.configReports && data.configReports?.length > 0) {
      data.configReports.forEach((obj: IReportsDetails) => {
        const reportsDetails = reportsdetailsRow.map((x) => {
          return {
            reportsName: x.reportName,
            sourceSystem: x.sourceSystem,
            path: x.scriptPath,
            script: x.script,
          };
        });

        const reportsObj = {
          reportsName: obj.reportName,
          sourceSystem: obj.sourceSystem,
          path: obj.scriptPath,
          script: obj.script,
        };
        if (
          reportsDetails.some(
            (e) =>
              Object.entries(e).toString() ===
              Object.entries(reportsObj).toString()
          )
        ) {
          hasDuplicates = true;
          toast.fire(
            "Duplicate Reports Row",
            "Report Details has duplicate rows",
            "error"
          );
        } else {
          reportsdetailsRow.push(obj);
        }
      });
    }

    if (hasDuplicates === false) {
      setNewData({
        ...data,
        configReports: reportsdetailsRow,
      });
      setSaveModal(true);
    }
  };
  useEffect(() => {
    if (actionAddState) {
      setValue("isActive", true);
    }
  }, [actionAddState]);

  const handleSave = () => {
    setSaveModal(false);
    if (actionAddState) {
      addJobs(newData);
    } else {
      updateJobs(newData);
    }
  };
  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowToEdit(undefined);
    setActionAddState(false);
    setActionEditState(false);
  };

  if (addSuccess && addResult) {
    if (addResult.data === "duplicate") {
      toast.fire("Failed", "Event Name Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (addResult.data === "duplicate report") {
      toast.fire("Failed", "Report Name Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (addResult.data === "Error scheduling job in Hangfire") {
      toast.fire("Failed", "Unable to add Job in hangfire", "error");
      queryClient.invalidateQueries("GetJobs");
      setActionAddState(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      queryClient.invalidateQueries("GetJobs");
      handleCancel();
      toast.fire("Success", "Events Added Successfully", "success");
      setIsScriptChange(false);
    } else if (addResult.status === 500) {
      if (
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Event added successfully, but " + addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire("Failed", "Failed to Add Event ! No Data Saved", "error");
      }
      queryClient.invalidateQueries("GetJobs");
      setActionAddState(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Add Events! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetJobs");
      setActionAddState(false);
    }
    resetAddData();
    setSaveModal(false);
    setIsSaving(false);
  }
  if (addError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire("Server Error", "Failed to Add Event! No Data Saved", "error");
    queryClient.invalidateQueries("Getjobs");
    setActionAddState(false);
  }

  if (editSuccess && editResult) {
    if (editResult.data === "duplicate") {
      toast.fire("Failed", "Event Name Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (editResult.data === "Error scheduling job in Hangfire") {
      toast.fire("Failed", "Unable to update Job in hangfire", "error");
      queryClient.invalidateQueries("GetJobs");
      setActionEditState(false);
    } else if (editResult.status === 201 || editResult.status === 200) {
      queryClient.invalidateQueries("GetJobs");
      queryClient.invalidateQueries("getJobDetailsById");
      refetchJobs();
      handleCancel();
      toast.fire("Success", "Events Updated Successfully", "success");
    } else if (editResult.data === "duplicate report") {
      toast.fire("Failed", "Report Name Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (editResult.status === 500) {
      if (
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        editResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "Events Updated successfully, but " + editResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire("Failed", "Failed to Update Event ! No Data Saved", "error");
      }
      queryClient.invalidateQueries("GetJobs");
      setActionEditState(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Update Events! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetJobs");
      setActionEditState(false);
    }
    editResetData();
    setSaveModal(false);
    setIsSaving(false);
  }

  if (editError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Update Events ! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("Getjobs");
    setActionEditState(false);
  }
  return (
    <div>
      <div className="p-3 space-y-2 border-l-2 border-primary box-shadow mt-2">
        <FormProvider {...useFormMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-2">
              <div className="ml-2 font-bold text-primary text-md">
                {actionAddState ? "Add New Event Name" : "Edit Event Details"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={isSaving}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={() => {
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="border-b-2 border-secondary mr-2 ml-2"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div className="">
            <JobFormDetails
              rowToEdit={editForRow}
              scheduleDaysOpts={scheduleDaysOpts}
              monthDaysOpts={monthDaysOpts}
            />
          </div>
        </FormProvider>
      </div>
      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={actionAddState ? "Add Event Name" : "Update Event Name"}
          qConfirmation={`Are you sure you want to ${
            actionAddState ? "Add " : "Update "
          } this ?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            handleSave();
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </div>
  );
};
export default JobForm;
